import React from 'react';
import { Reveal } from '@/components/public/anim';
import { Leaf, Mountain, Trees, Globe2, Award } from 'lucide-react';

// Sintesi (ben scritta) del testo fornito dal Parco. Editabile dal CRM admin
// (Pagina "Il Parco della Sila", campo "Testo introduttivo"). Vuoto = questo default.
const DEFAULT_INTRO =
  "Istituito ufficialmente nel 1997 ma operativo già dal 1968, il Parco Nazionale della Sila si estende sul più "
  + "grande altopiano d'Europa — tra Sila Grande, Sila Piccola e Sila Greca — nelle province di Cosenza, Catanzaro e "
  + "Crotone. La sua missione è tutelare gli ambienti di maggior pregio, sostenere le attività tradizionali "
  + "(agro-silvo-pastorali, artigianali, enogastronomiche e turistiche) e promuovere uno sviluppo economico e sociale "
  + "ecocompatibile per le comunità del territorio.\n\n"
  + "Il Parco mette a disposizione un Geoportale — un sistema informativo territoriale online, aperto a tutti — e lavora "
  + "con Europarc Federation e Federparchi al rinnovo della Carta Europea del Turismo Sostenibile (CETS), un percorso che "
  + "sostiene anche la candidatura a Patrimonio Mondiale UNESCO. La Sila è inoltre Riserva della Biosfera UNESCO MAB: "
  + "355.000 ettari e 66 Comuni dove attività umane, natura e insediamenti convivono in armonia.";

const FACTS = [
  { icon: Mountain, text: "Il più grande altopiano d'Europa" },
  { icon: Trees, text: '355.000 ettari · 66 Comuni' },
  { icon: Globe2, text: 'Geoportale aperto a tutti' },
  { icon: Award, text: 'Riserva della Biosfera UNESCO MAB' },
];

const paragraphs = (text) => String(text || '').split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);

/**
 * Sezione "Il Parco della Sila": un unico blocco orizzontale con la sintesi
 * descrittiva a sinistra e i fatti chiave a destra. Mostrata sopra le guide.
 */
export const ParkInfoSection = ({ content = {} }) => {
  const intro = (typeof content.intro === 'string' && content.intro.trim()) ? content.intro : DEFAULT_INTRO;

  return (
    <section className="sila-section py-14 sm:py-20" data-testid="park-info-section">
      <Reveal>
        <div className="overflow-hidden rounded-3xl border border-border bg-card">
          <div className="grid gap-8 p-7 sm:p-10 lg:grid-cols-[1.6fr_1fr] lg:items-center">
            {/* Sintesi */}
            <div>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--brand-sun)/0.22)] px-3 py-1 text-xs font-semibold uppercase tracking-wide text-[hsl(var(--brand-bark))]">
                <Leaf className="h-3.5 w-3.5" />Il Parco Nazionale della Sila
              </span>
              <h2 className="mt-4 font-display text-2xl font-bold tracking-tight sm:text-3xl">
                {content.title || 'Il Parco della Sila'}
              </h2>
              <div className="mt-4 space-y-3">
                {paragraphs(intro).map((p, i) => (
                  <p key={i} className="text-[15px] leading-relaxed text-muted-foreground">{p}</p>
                ))}
              </div>
            </div>

            {/* Fatti chiave */}
            <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1 lg:border-l lg:border-border lg:pl-8">
              {FACTS.map((f, i) => (
                <li key={i} className="flex items-center gap-3" data-testid={`park-fact-${i}`}>
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                    <f.icon className="h-5 w-5" />
                  </span>
                  <span className="text-sm font-medium text-foreground/90">{f.text}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Reveal>
    </section>
  );
};

export default ParkInfoSection;
